import React from 'react';
import {
  Download,
  Eye,
  User,
  FileText,
  ChevronLeft,
  Clock,
  Gavel,
  BookOpen,
  File,
  AlignLeft,
  Edit,
  Trash2,
  TestTube,
  X,
  Save,
  Star
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api, { getPublishers, getOrganizations, getDocumentTypes, CourtType, getCourtTypes } from '../services/api';
import AutoComplete from './AutoComplete';
import type { Document } from '../types/document';
import { useAuth } from '../contexts/AuthContext';
import { PdfViewer, TextContentViewer } from './DocumentPreviewModal';


export interface DocumentType {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

// Utility function for downloading files
const downloadFile = async (documentId: number, fileName: string) => {
  try {
    const response = await api.get(`/api/documents/${documentId}/download`, {
      responseType: 'blob'
    });

    // Create blob link to download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    toast.success('Download started');
  } catch (error: any) {
    toast.error(error.response?.data?.error || 'Download failed');
  }
};

// Utility function for deleting documents
const deleteDocument = async (documentId: number, onSuccess: () => void) => {
  if (!window.confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
    return;
  }

  try {
    await api.delete(`/api/documents/${documentId}`);
    toast.success('Document deleted successfully');
    onSuccess();
  } catch (error: any) {
    toast.error(error.response?.data?.error || 'Failed to delete document');
  }
};

// Document Grid Component
export const DocumentGrid: React.FC<{
  documents: Document[];
  onDocumentClick: (doc: Document) => void;
  onEdit: (doc: Document) => void;
  onDelete: (docId: number) => void;
  favorites?: Set<number>;
  onToggleFavorite?: (doc: Document) => void;
}> = ({
  documents,
  onDocumentClick,
  onEdit,
  onDelete,
  favorites,
  onToggleFavorite
}) => {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 md:gap-6 mb-8">
        {documents.map((document) => (
          <DocumentCard
            key={document.id}
            document={document}
            onClick={() => onDocumentClick(document)}
            onEdit={() => onEdit(document)}
            onDelete={() => onDelete(document.id)}
            isFavorite={favorites?.has(document.id) || false}
            onToggleFavorite={onToggleFavorite ? () => onToggleFavorite(document) : undefined}
          />
        ))}
      </div>
    );
  };

// Document List Component
export const DocumentList: React.FC<{
  documents: Document[];
  onDocumentClick: (doc: Document) => void;
  onEdit: (doc: Document) => void;
  onDelete: (docId: number) => void;
  favorites?: Set<number>;
  onToggleFavorite?: (doc: Document) => void;
}> = ({
  documents,
  onDocumentClick,
  onEdit,
  onDelete,
  favorites,
  onToggleFavorite
}) => {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-8">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Document
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Uploader
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Size
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {documents.map((document) => (
                <DocumentRow
                  key={document.id}
                  document={document}
                  onClick={() => onDocumentClick(document)}
                  onEdit={() => onEdit(document)}
                  onDelete={() => onDelete(document.id)}
                  isFavorite={favorites?.has(document.id) || false}
                  onToggleFavorite={onToggleFavorite ? () => onToggleFavorite(document) : undefined}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

// Document Card Component
const DocumentCard: React.FC<{
  document: Document;
  onClick: () => void;
  onEdit: () => void;
  onDelete: () => void;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
}> = ({ document, onClick, onEdit, onDelete, isFavorite = false, onToggleFavorite }) => {
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getDocumentTypeIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'judgement':
        return <Gavel className="w-5 h-5" />;
      case 'article':
        return <FileText className="w-5 h-5" />;
      case 'commentary':
        return <BookOpen className="w-5 h-5" />;
      case 'test document':
        return <TestTube className="w-5 h-5" />;
      default:
        return <FileText className="w-5 h-5" />;
    }
  };

  const getFileTypeIcon = (fileType: string) => {
    switch (fileType.toLowerCase()) {
      case 'pdf':
        return <FileText className="w-4 h-4 text-red-500" />;
      case 'docx':
      case 'doc':
        return <File className="w-4 h-4 text-blue-500" />;
      case 'txt':
        return <AlignLeft className="w-4 h-4 text-gray-500" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const { user } = useAuth();

  return (
    <div
      className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow duration-200 cursor-pointer"
      onClick={onClick}
    >
      <div className="p-4 md:p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-2">
            <div className="text-blue-600 dark:text-blue-400">
              {getDocumentTypeIcon(document.document_type)}
            </div>
            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${document.document_type.toLowerCase() === 'test document'
              ? 'text-orange-800 bg-orange-100 dark:bg-orange-900 dark:text-orange-300'
              : 'text-blue-800 bg-blue-100 dark:bg-blue-900 dark:text-blue-300'
              }`}>
              {document.document_type}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${document.is_indexed
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
              }`}>
              {document.is_indexed ? 'Indexed' : 'Pending'}
            </span>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2">
          {document.title}
        </h3>

        {document.summary && (
          <p className="text-gray-600 dark:text-gray-300 text-sm mb-4 line-clamp-3">
            {document.summary}
          </p>
        )}

        <div className="space-y-2 text-sm text-gray-500 dark:text-gray-400">
          {document.citation && (
            <div className="flex items-center space-x-2">
              <BookOpen className="w-4 h-4" />
              <span className="truncate max-w-48" title={document.citation}>{document.citation}</span>
            </div>
          )}

          <div className="flex items-center space-x-2">
            <User className="w-4 h-4" />
            <span className="truncate max-w-32" title={document.uploader_name}>{document.uploader_name}</span>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Clock className="w-4 h-4" />
              <span>{new Date(document.created_at).toLocaleDateString()}</span>
            </div>
            <span className="text-xs min-w-16 text-right">{formatFileSize(document.file_size)}</span>
          </div>
        </div>

        {/* Action Buttons */}

        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600 flex justify-end space-x-2">
          {onToggleFavorite && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleFavorite();
              }}
              className={`p-2 rounded-lg transition-colors ${isFavorite
                  ? 'text-yellow-500 hover:text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/30'
                  : 'text-gray-400 hover:text-yellow-500 hover:bg-yellow-50 dark:hover:bg-yellow-900/30'
                }`}
              title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            >
              <Star className="w-4 h-4" fill={isFavorite ? 'currentColor' : 'none'} />
            </button>
          )}
          {((user?.role === 'document_admin' || user?.role === 'super_admin')) && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 dark:text-gray-400 dark:hover:text-blue-400 dark:hover:bg-blue-900 rounded-lg transition-colors"
              title="Edit document"
            >
              <Edit className="w-4 h-4" />
            </button>

          )}
          {user?.role === 'super_admin' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                deleteDocument(document.id, onDelete);
              }}
              className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 dark:text-gray-400 dark:hover:text-red-400 dark:hover:bg-red-900 rounded-lg transition-colors"
              title="Delete document"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>

      </div>
    </div>
  );
};

// Document Row Component (for table view)
const DocumentRow: React.FC<{
  document: Document;
  onClick: () => void;
  onEdit: () => void;
  onDelete: () => void;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
}> = ({ document, onClick, onEdit, onDelete, isFavorite = false, onToggleFavorite }) => {
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getDocumentTypeIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'judgement':
        return <Gavel className="w-4 h-4" />;
      case 'article':
        return <FileText className="w-4 h-4" />;
      case 'commentary':
        return <BookOpen className="w-4 h-4" />;
      case 'test document':
        return <TestTube className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getFileTypeIcon = (fileType: string) => {
    switch (fileType.toLowerCase()) {
      case 'pdf':
        return <FileText className="w-4 h-4 text-red-500" />;
      case 'docx':
      case 'doc':
        return <File className="w-4 h-4 text-blue-500" />;
      case 'txt':
        return <AlignLeft className="w-4 h-4 text-gray-500" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const { user } = useAuth();

  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer" onClick={onClick}>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="text-blue-600 dark:text-blue-400 mr-3">
            {getDocumentTypeIcon(document.document_type)}
          </div>
          <div className="mr-3">
            {getFileTypeIcon(document.file_type)}
          </div>
          <div>
            <div className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-xs">
              {document.title}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-sm" title={document.file_name}>
              {document.file_name}
            </div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${document.document_type.toLowerCase() === 'test document'
          ? 'text-orange-800 bg-orange-100 dark:bg-orange-900 dark:text-orange-300'
          : 'text-blue-800 bg-blue-100 dark:bg-blue-900 dark:text-blue-300'
          }`}>
          {document.document_type}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
        {document.date_of_document ? new Date(document.date_of_document).toLocaleDateString() : 'N/A'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
        {document.uploader_name}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
        {formatFileSize(document.file_size)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        {onToggleFavorite && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite();
            }}
            className={`mr-2 transition-colors ${isFavorite
                ? 'text-yellow-500 hover:text-yellow-600'
                : 'text-gray-400 hover:text-yellow-500'
              }`}
            title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            <Star className="w-4 h-4" fill={isFavorite ? 'currentColor' : 'none'} />
          </button>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            downloadFile(document.id, document.file_name);
          }}
          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-2"
          title="Download"
        >
          <Download className="w-4 h-4" />
        </button>

        {((user?.role === 'document_admin' || user?.role === 'super_admin')) && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            className="text-gray-600 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 mr-2"
            title="Edit"
          >
            <Edit className="w-4 h-4" />
          </button>
        )}

        {(user?.role === 'super_admin') && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              deleteDocument(document.id, onDelete);
            }}
            className="text-gray-600 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 mr-2"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}

        <button
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
          className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
          title="View"
        >
          <Eye className="w-4 h-4" />
        </button>
      </td>
    </tr>
  );
};

// Document Detail View Component
export const DocumentDetailView: React.FC<{
  document: Document;
  onBack: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}> = ({ document, onBack, onEdit, onDelete }) => {
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getDocumentTypeIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'judgement':
        return <Gavel className="w-6 h-6" />;
      case 'article':
        return <FileText className="w-6 h-6" />;
      case 'commentary':
        return <BookOpen className="w-6 h-6" />;
      case 'test document':
        return <TestTube className="w-6 h-6" />;
      default:
        return <FileText className="w-6 h-6" />;
    }
  };

  const getFileTypeIcon = (fileType: string) => {
    switch (fileType.toLowerCase()) {
      case 'pdf':
        return <FileText className="w-6 h-6 text-red-500" />;
      case 'docx':
      case 'doc':
        return <File className="w-6 h-6 text-blue-500" />;
      case 'txt':
        return <AlignLeft className="w-6 h-6 text-gray-500" />;
      default:
        return <FileText className="w-6 h-6" />;
    }
  };

  const handleDownload = () => downloadFile(document.id, document.file_name);
  const { user } = useAuth();
  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={onBack}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <ChevronLeft className="w-5 h-5" />
          <span>Back to Documents</span>
        </button>

        <div className="flex items-center space-x-3">
          <button
            onClick={handleDownload}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <Download className="w-4 h-4" />
            <span>Download</span>
          </button>

          {(user?.role === 'document_admin' || user?.role === 'super_admin') && onEdit && (
            <button
              onClick={onEdit}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              <Edit className="w-4 h-4" />
              <span>Edit</span>
            </button>
          )}

          {(user?.role === 'super_admin') && onDelete && (
            <button
              onClick={() => deleteDocument(document.id, onDelete)}
              className="flex items-center space-x-2 px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:border-red-600 dark:text-red-400 dark:hover:bg-red-900"
            >
              <Trash2 className="w-4 h-4" />
              <span>Delete</span>
            </button>
          )}
          {/**/}
          {/* <button className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700">
            <Heart className="w-4 h-4" />
            <span>Add to Favorites</span>
          </button> */}
        </div>
      </div>

      {/* Document Info */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
        <div className="p-6">
          <div className="flex items-start space-x-4 mb-6">
            <div className="text-blue-600 dark:text-blue-400">
              {getDocumentTypeIcon(document.document_type)}
            </div>
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {document.title}
                </h1>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${document.is_indexed
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                  : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                  }`}>
                  {document.is_indexed ? 'Indexed' : 'Pending Index'}
                </span>
              </div>
              <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                <span className="flex items-center space-x-1">
                  <FileText className="w-4 h-4" />
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${document.document_type.toLowerCase() === 'test document'
                    ? 'text-orange-800 bg-orange-100 dark:bg-orange-900 dark:text-orange-300'
                    : 'text-blue-800 bg-blue-100 dark:bg-blue-900 dark:text-blue-300'
                    }`}>
                    {document.document_type}
                  </span>
                </span>
                <span className="flex items-center space-x-1">
                  <User className="w-4 h-4" />
                  <span>Uploaded by {document.uploader_name}</span>
                </span>
                <span className="flex items-center space-x-1">
                  <Clock className="w-4 h-4" />
                  <span>{new Date(document.created_at).toLocaleDateString()}</span>
                </span>
              </div>
            </div>
          </div>

          {/* Metadata Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Document Details</h3>

              {document.summary && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Summary
                  </label>
                  <p className="text-gray-600 dark:text-gray-400">{document.summary}</p>
                </div>
              )}

              {document.citation && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Citation
                  </label>
                  <p className="text-gray-600 dark:text-gray-400">{document.citation}</p>
                </div>
              )}

              {document.date_of_document && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Document Date
                  </label>
                  <p className="text-gray-600 dark:text-gray-400">
                    {new Date(document.date_of_document).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Additional Information</h3>

              {document.author && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Author
                  </label>
                  <p className="text-gray-600 dark:text-gray-400">{document.author}</p>
                </div>
              )}

              {document.topics && document.topics.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Topics
                  </label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {document.topics.map((topic, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                      >
                        {topic}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {document.publisher && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Publisher
                  </label>
                  <p className="text-gray-600 dark:text-gray-400">{document.publisher}</p>
                </div>
              )}

              {document.organization && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Organization
                  </label>
                  <p className="text-gray-600 dark:text-gray-400">{document.organization}</p>
                </div>
              )}

              {document.court && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Court
                  </label>
                  <p className="text-gray-600 dark:text-gray-400">{document.court}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  File Information
                </label>
                <div className="text-gray-600 dark:text-gray-400 space-y-1">
                  <p className="break-all">
                    <span className="font-medium">File:</span>
                    <span className="ml-1 break-all">{document.file_name}</span>
                  </p>
                  <p>Size: {formatFileSize(document.file_size)}</p>
                  <p>Type: {document.file_type.toUpperCase()}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Document Preview */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Document Preview</h3>
            <div className="flex items-center space-x-2">
              {getFileTypeIcon(document.file_type)}
              <span className="text-sm text-gray-500 dark:text-gray-400 uppercase">
                {document.file_type} File
              </span>
            </div>
          </div>
          <DocumentViewer document={document} />
        </div>
      </div>
    </div>
  );
};

// Document Viewer Component
const DocumentViewer: React.FC<{ document: Document }> = ({ document }) => {
  const getFileTypeIcon = (fileType: string) => {
    switch (fileType.toLowerCase()) {
      case 'pdf':
        return <FileText className="w-16 h-16 text-red-500" />;
      case 'docx':
      case 'doc':
        return <File className="w-16 h-16 text-blue-500" />;
      case 'txt':
        return <AlignLeft className="w-16 h-16 text-gray-500" />;
      default:
        return <FileText className="w-16 h-16" />;
    }
  };

  const handleDownload = () => downloadFile(document.id, document.file_name);

  if (document.file_type && ['pdf', 'doc', 'docx'].includes(document.file_type.toLowerCase())) {
    return (
      <div className="mt-4">
        <PdfViewer documentId={document.id} />
      </div>
    );
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 min-h-96">
      <TextContentViewer documentId={document.id} fileType={document.file_type || 'txt'} />

      {/* Download button for full document */}
      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600 flex justify-end">
        <button
          onClick={handleDownload}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
        >
          <Download className="w-4 h-4" />
          <span>Download Original File</span>
        </button>
      </div>
    </div>
  );
};

// Document Edit Modal Component
export const DocumentEditModal: React.FC<{
  document: Document | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedDoc: Partial<Document>) => void;
}> = ({ document, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = React.useState({
    title: '',
    document_type: '',
    summary: '',
    citation: '',
    publisher: '',
    organization: '',
    court: '',
    author: '',
    date_of_document: ''
  });
  const [topics, setTopics] = React.useState<string[]>([]);
  const [currentTopic, setCurrentTopic] = React.useState('');

  const addTopic = (e?: React.MouseEvent | React.KeyboardEvent) => {
    if (e) e.preventDefault();
    const trimmed = currentTopic.trim();
    if (trimmed && !topics.includes(trimmed)) {
      setTopics([...topics, trimmed]);
      setCurrentTopic('');
    }
  };

  const removeTopic = (topicToRemove: string) => {
    setTopics(topics.filter(t => t !== topicToRemove));
  };
  const [publishers, setPublishers] = React.useState<string[]>([]);
  const [publishersLoading, setPublishersLoading] = React.useState(false);
  const [organizations, setOrganizations] = React.useState<string[]>([]);
  const [organizationsLoading, setOrganizationsLoading] = React.useState(false);
  const [documentTypes, setDocumentTypes] = React.useState<DocumentType[]>([]);
  const [documentTypesLoading, setDocumentTypesLoading] = React.useState(false);
  const [courtTypes, setCourtTypes] = React.useState<CourtType[]>([]);
  const [courtTypesLoading, setCourtTypesLoading] = React.useState(false);

  React.useEffect(() => {
    if (document) {
      setFormData({
        title: document.title || '',
        document_type: document.document_type || '',
        summary: document.summary || '',
        citation: document.citation || '',
        publisher: document.publisher || '',
        organization: document.organization || '',
        court: document.court || '',
        author: document.author || '',
        date_of_document: document.date_of_document || ''
      });
      setTopics(document.topics || []);
      setCurrentTopic('');
    }
  }, [document]);

  // Load publishers and organizations when modal opens
  React.useEffect(() => {
    if (isOpen) {
      const loadData = async () => {
        // Load publishers
        setPublishersLoading(true);
        try {
          const publishersData = await getPublishers();
          setPublishers(publishersData);
        } catch (error) {
          console.error('Failed to load publishers:', error);
        } finally {
          setPublishersLoading(false);
        }

        // Load document types
        setDocumentTypesLoading(true);
        try {
          const documentTypesData = await getDocumentTypes();
          setDocumentTypes(documentTypesData);
        } catch (error) {
          console.error('Failed to load document types:', error);
        } finally {
          setDocumentTypesLoading(false);
        }

        // Load court types
        setCourtTypesLoading(true);
        try {
          const courtTypesData = await getCourtTypes();
          setCourtTypes(courtTypesData);
        } catch (error) {
          console.error('Failed to load court types:', error);
        } finally {
          setCourtTypesLoading(false);
        }
        // Load organizations
        setOrganizationsLoading(true);
        try {
          const organizationsData = await getOrganizations();
          setOrganizations(organizationsData);
        } catch (error) {
          console.error('Failed to load organizations:', error);
        } finally {
          setOrganizationsLoading(false);
        }
      };

      loadData();
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ ...formData, topics });
  };

  // const documentTypes = [
  //   'Judgement',
  //   'Article', 
  //   'Commentary',
  //   'Case Study',
  //   'Legal Opinion',
  //   'Contract',
  //   'Statute',
  //   'Regulation',
  //   'Court Filing',
  //   'Other'
  // ];

  if (!isOpen || !document) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-600">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Edit Document
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Title
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Document Type
            </label>
            <select
              value={formData.document_type}
              onChange={(e) => setFormData({ ...formData, document_type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              required
            >
              <option value="">Select document type</option>
              {documentTypes.map((type) => (
                <option key={type.id} value={type.name}>{type.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Summary
            </label>
            <textarea
              value={formData.summary}
              onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Author
            </label>
            <input
              type="text"
              value={formData.author}
              onChange={(e) => setFormData({ ...formData, author: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="Document's author or issuing authority"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Topics
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={currentTopic}
                onChange={(e) => setCurrentTopic(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addTopic();
                  }
                }}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="Type a topic and hit Enter or Add"
              />
              <button
                type="button"
                onClick={addTopic}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg font-medium transition-colors duration-200"
              >
                Add
              </button>
            </div>

            {topics.length > 0 && (
              <div className="mt-3 flex overflow-x-auto whitespace-nowrap space-x-2 pb-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
                {topics.map((topic, index) => (
                  <span
                    key={index}
                    className="inline-flex flex-shrink-0 items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                  >
                    {topic}
                    <button
                      type="button"
                      onClick={() => removeTopic(topic)}
                      className="ml-2 inline-flex items-center text-blue-400 hover:text-blue-600 dark:hover:text-blue-200 focus:outline-none"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Citation
              </label>
              <input
                type="text"
                value={formData.citation}
                onChange={(e) => setFormData({ ...formData, citation: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Publisher
              </label>
              <AutoComplete
                value={formData.publisher}
                onChange={(value) => setFormData({ ...formData, publisher: value })}
                placeholder="Publisher name"
                options={publishers}
                loading={publishersLoading}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Organization
              </label>
              <AutoComplete
                value={formData.organization}
                onChange={(value) => setFormData({ ...formData, organization: value })}
                placeholder="Organization name"
                options={organizations}
                loading={organizationsLoading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Court
              </label>
              <select
                value={formData.court}
                onChange={(e) => setFormData({ ...formData, court: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                required
              >
                <option value="">Select Court</option>
                {courtTypes.map((type) => (
                  <option key={type.id} value={type.name}>{type.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Date of Document
            </label>
            <input
              type="date"
              value={formData.date_of_document}
              onChange={(e) => setFormData({ ...formData, date_of_document: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Save className="w-4 h-4" />
              <span>Save Changes</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};